import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Platform,
  ActivityIndicator
} from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import TransferSummaryModal from "../../../common/steps/TransferSummaryComponent";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import { sharePayload } from "../../../../uiComponents/ShareUtils";
import CopyIcon from "../../../../../../../../assets/images/copy.svg";
import ReceiptIcon from "../../../../../../../../assets/images/receipt.svg";
import ShareIcon from "../../../../../../../../assets/images/share.ios.svg";
import copyToClipboard from "../../../../../helper/copyToClipBoard";
import Colors from "../../../../../constants/colors";
import AnimatedSuccessIcon from "../../../common/AnimatedSuccessView";

// Example props interface
interface HeadToCashierStepProps {
  status?: string; // e.g. "Staged"
  receiveAmount?: string;
  receiveCurrency?: string;
  onViewSummary?: () => void;
  onShareReceipt?: () => void;
  testID?: string;
}

const BillPayHeadToCashierStep: React.FC<HeadToCashierStepProps> = ({
  receiveAmount,
  receiveCurrency,
  status = "Staged",
  onViewSummary,
  testID,
  // onShareReceipt,
}) => {
  const { t } = useTranslation();
  const isBillPay = useSessionStore((s: SessionState) => (s as any).isBillPay);
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  const [summaryVisible, setSummaryVisible] = React.useState(false);
  const [sharing, setSharing] = useState(false);

  // Pull values from store
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const receiverFirstName = useSessionStore((s: SessionState) => (s as any).receiverFirstName);
  const receiverMiddleName = useSessionStore((s: SessionState) => (s as any).receiverMiddleName);
  const receiverLastName = useSessionStore((s: SessionState) => (s as any).receiverLastName);
  const receiverLastName2 = useSessionStore((s: SessionState) => (s as any).receiverLastName2);
  const confirmationNumber = useSessionStore((s: SessionState) => (s as any).confirmationNumber);

  const option: any = pendingSendOption || selectedSendOption || null;
  const selectedBillerInformation = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);
  const billPayValidationResponseStore = useSessionStore((s: SessionState) => (s as any).billPayValidationResponseStore);

  const storeReceiveCurrency = option ? FeeLookupHelper.getReceiveCurrency(option) : "";
  const receiveAmountNum = option ? FeeLookupHelper.getReceiveAmount(option) : 0;
  // Prefer SendAmount from validation response for display
  const { sendAmountDisplay, sendCurrencyDisplay } = useMemo(() => {
    try {
      const raw: any = billPayValidationResponseStore as any;
      const sendAmtRaw: any = raw?.SendAmount?.SendAmount;
      const sendCurr: string = String( raw?.SendAmount?.SendCurrency || 'USD');
      const n = Number(String(sendAmtRaw ?? 0).replace(/,/g, ''));
      const formatted = isFinite(n)
        ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '0.00';
      return { sendAmountDisplay: formatted, sendCurrencyDisplay: sendCurr };
    } catch {
      const fallbackNum = Number(option?.SendAmounts?.SendAmount ?? 0);
      const formatted = isFinite(fallbackNum)
        ? fallbackNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '0.00';
      const sendCurr: string = (option && (option.SendAmounts?.SendCurrency || 'USD')) || 'USD';
      return { sendAmountDisplay: formatted, sendCurrencyDisplay: sendCurr };
    }
  }, [billPayValidationResponseStore, option]);

  // Keep receive amount string for receipt/share and modal
  const amount = useMemo(() => {
    const num = Number(receiveAmountNum || 0);
    return isFinite(num)
      ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "0.00";
  }, [receiveAmountNum]);
  const receiverName = useMemo(() =>
    [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2].filter(Boolean).join(" "),
    [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]
  );
  const billerName = useMemo(() => String(selectedBillerInformation?.BillerName ?? selectedBillerInformation?.billerName ?? ''), [selectedBillerInformation]);
  const referenceNumber = confirmationNumber || "";
  // Additional computed values for modal
  const sendCurrency = (billPayValidationResponseStore && (billPayValidationResponseStore?.SendAmount?.SendCurrency || "USD")) || "USD";
  const sendAmountNum = Number(billPayValidationResponseStore?.SendAmount?.SendAmount ?? 0);
  const exchangeRateNum = option ? FeeLookupHelper.getExchangeRate(option) : 0;
  const feesNum = Number(billPayValidationResponseStore?.SendAmount?.TotalDiscountAmount || 0) + Number(billPayValidationResponseStore?.SendAmount?.TotalSendFees || 0);
  const totalNum = Number(billPayValidationResponseStore?.TotalSendAmount || (sendAmountNum + feesNum));

  const buildReceiptText = () => {
    return `MoneyGram Receipt\n\nReceiver: ${receiverName}\nAmount: ${receiveAmount} ${receiveCurrency}\nReference: ${referenceNumber}\nStatus: Staged\n\nThank you for using MoneyGram.`;
  };


  const onShare = useCallback(async () => {
    setSharing(true);
    try {
      const payload = {
        title: "MoneyGram Receipt",
        message: buildReceiptText(),
        // If you have a receipt URL, add it here (helps web share)
        // url: 'https://app.example.com/receipt/0000123456'
      };

      const result = await sharePayload(payload);
      // result has meta info about which path was used
      if (Platform.OS === "web") {
        if ((result as any).usedWebShare) {
          // navigator.share resolved — nothing else to do
          console.log("Shared via navigator.share", result);
        } else if ((result as any).usedClipboard) {
          Alert.alert(
            "Share not available",
            "Share sheet is not supported in this browser. Receipt text has been copied to clipboard — paste it into any app to share."
          );
        } else {
          Alert.alert("Shared", "Sharing completed.");
        }
      } else {
        // Native platforms
        console.log("Native share result:", result);
      }
    } catch (err: any) {
      console.error("Share error:", err);
      Alert.alert("Unable to share", err?.message || "An error occurred while sharing.");
    } finally {
      setSharing(false);
    }
  }, [receiverName, amount, storeReceiveCurrency, referenceNumber]);

  return (
    <View style={styles.container} testID={testID}>
      <View style={{alignItems: 'center'}}>
        <AnimatedSuccessIcon />
        <Text style={styles.title}>{t("HEAD_TO_CASHIER")}</Text>
        <Text style={styles.subtitle}>{t("THANK_YOU_MONEYGRAM")}</Text>
        <Text style={styles.receiverLine}>
          {billerName} {t("WILL_RECEIVE")}
        </Text>
        <Text style={styles.amount}>{sendAmountDisplay} <Text style={styles.currencyStyle}>{sendCurrencyDisplay}</Text></Text>
        <Text style={[styles.referenceLabel, { marginBottom: 9}]}>
          {t("STATUS")} <Text style={styles.referenceNumber}>{status}</Text>
        </Text>
      </View>
      <View style={styles.referenceRow}>
        <Text style={styles.referenceLabel}>{t("REFERENCE")}</Text>
        <TouchableOpacity style={styles.referenceCopy} onPress={() => copyToClipboard(referenceNumber)}
          testID="referenceNumberCopy">
          <Text style={styles.referenceNumber} testID="billPayReferenceNumber">{referenceNumber}</Text>
          <CopyIcon width={24} height={24}/>
        </TouchableOpacity>
      </View>
      <Text style={styles.instructionTitle}>{t("WHAT_DO_I_SAY")}</Text>
      <Text style={styles.instruction}>
        {t("LET_THE_CASHIER_KNOW")}
      </Text>
      <View style={styles.actionsRow}>
        <TouchableOpacity
          style={[styles.actionBox, { marginRight: 12 }]}
          onPress={() => setSummaryVisible(true)}
          testID="viewTransferSummary">
          <View style={styles.receiptView}>
            <ReceiptIcon width={24} height={24}/>
          </View>
          <Text style={styles.actionLabel}>{t("VIEW_DETAILS")}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionBox}
          onPress={onShare}
          testID="shareReceiptButton"
        >
          <View style={styles.receiptView}>
            <ShareIcon width={24} height={24}/>
          </View>
          <Text style={styles.actionLabel}>{t("SHARE_RECEIPT")}</Text>
        </TouchableOpacity>
      </View>
      <TransferSummaryModal
        visible={summaryVisible}
        onClose={() => setSummaryVisible(false)}
        receiverName={billerName || ""}
        amount={`${(isFinite(sendAmountNum) ? sendAmountNum : 0).toFixed(2)} ${sendCurrency}`}
        status="Payment Processing"
        paymentMethod={"Cash"}
        exchangeRate={`1 ${sendCurrency} = ${exchangeRateNum.toFixed(4)} ${storeReceiveCurrency}`}
        fees={`${feesNum.toFixed(2)} ${sendCurrency}`}
        youSent={`${(isFinite(sendAmountNum) ? sendAmountNum : 0).toFixed(2)} ${sendCurrency}`}
        total={`${totalNum.toFixed(2)} ${sendCurrency}`}
        receiveMethod={option?.DeliveryOption || undefined}
        isBillPay= {isBillPay}
      />
        {sharing && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={styles.overlayText}>Preparing share …</Text>
        </View>
      )}
    </View>
  );
};

export default BillPayHeadToCashierStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  title: {
    fontSize: 28,
    color: Colors.darkGrey,
    fontWeight: '500',
    fontFamily: 'Rubik',
    lineHeight: 34,
    marginBottom: 8,
    marginTop: 24
  },
  subtitle: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '400',
    fontFamily: 'Rubik',
    marginBottom: 32,
  },
  receiverLine: {
    fontSize: 16,
    color: Colors.titleTextColor,
    fontWeight: '400',
    fontFamily: 'Rubik',
    lineHeight: 22,
    marginBottom: 6,
    textAlign: "center",
    width: "100%",
  },
  amount: {
    fontSize: 40,
    color: Colors.titleTextColor,
    fontWeight: '500',
    fontFamily: 'Rubik',
    marginBottom: 6,
    lineHeight: 46,
    textAlign: "center",
    width: "100%",
  },
  currencyStyle:{
    fontSize: 24,
    fontFamily: 'Rubik',
    fontWeight: '500',
    color: Colors.infoSubValue,
    lineHeight: 30
  },
  referenceRow: {
    flexDirection: "row",
    justifyContent: 'center',
    alignItems: "center",
    marginBottom: 36,
    backgroundColor: Colors.buttonTextColor,
    borderRadius: 8,
    paddingVertical: 12,
  },
  referenceLabel: {
    fontSize: 16,
    color: Colors.darkGrey,
    fontWeight: '500',
    fontFamily: 'Rubik',
    lineHeight: 24,
    marginRight: 12,
  },
  referenceCopy: {
    flexDirection: "row",
    alignItems: "center",
  },
  referenceNumber: {
    fontSize: 16,
    color: Colors.termsAndConditionsBlue,
    fontWeight: '400',
    fontFamily: 'Rubik',
    lineHeight: 26,
    marginRight: 12,
  },
  copyIcon: {
    marginLeft: 4,
    fontSize: 14,
  },
  instruction: {
    fontSize: 16,
    color: Colors.black,
    marginBottom: 24,
    fontFamily: 'Rubik',
    fontWeight: '400'
    // paddingHorizontal: 12,
  },
  instructionTitle: {
    fontSize: 20,
    color: Colors.darkGrey,
    lineHeight: 26,
    marginBottom: 8,
    fontFamily: 'Rubik',
    fontWeight: '400',
    textAlign: 'left'
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 39,
    width: "100%",
  },
  actionBox: {
    flex: 1,
    padding: 16,
    borderColor: "#ddd",
    backgroundColor: Colors.searchCompanyBackground,
    borderRadius: 16,
  },
  receiptView: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: 'center',
    backgroundColor: Colors.secondaryText,
    marginBottom: 12,
    borderRadius: 30,
  },
  actionIcon: {
    fontSize: 20,
    marginBottom: 6,
  },
  actionLabel: {
    fontSize: 16,
    color: Colors.titleTextColor,
    fontFamily: 'Rubik',
    fontWeight: '500',
    lineHeight: 24
  },
  shareBtn: {
    marginTop: 20,
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  shareText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  finishText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
  },
  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
  },
  overlayText: {
    color: "#fff",
    marginTop: 10,
  },
});